import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

const PRESETS = [10, 25, 100, 500];
const MIN_USD = 10;
const MAX_USD = 25_000;

// Amount picker → backend creates a hosted checkout session (mock page until
// Stripe lands) → full-page redirect. Credits land via the payment webhook,
// and the return URL (?checkout=success) refetches balances.
export function AddCreditsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [amount, setAmount] = useState<string>("25");

  const checkout = useMutation({
    mutationFn: (amountUsd: number) => api.billing.checkout(amountUsd),
    onSuccess: ({ checkoutUrl }) => {
      window.location.assign(checkoutUrl);
    },
  });

  const parsed = Number(amount);
  const valid =
    Number.isFinite(parsed) &&
    parsed >= MIN_USD &&
    parsed <= MAX_USD &&
    Math.round(parsed * 100) === parsed * 100;

  return (
    <Dialog open={open} onOpenChange={(o) => !checkout.isPending && onOpenChange(o)}>
      {/* Focus the amount field, not the first preset. */}
      <DialogContent
        className="max-w-sm"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          document.getElementById("credit-amount")?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Buy credits</DialogTitle>
          <DialogDescription>
            Choose an amount to add to your credit balance. You'll be taken to a
            secure checkout page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p}
                type="button"
                variant={Number(amount) === p ? "default" : "outline"}
                size="sm"
                onClick={() => setAmount(String(p))}
              >
                ${p}
              </Button>
            ))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="credit-amount" className="text-xs">
              Amount (USD)
            </Label>
            <Input
              id="credit-amount"
              type="number"
              min={MIN_USD}
              max={MAX_USD}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Minimum ${MIN_USD}, maximum ${MAX_USD.toLocaleString("en-US")} per purchase.
            </p>
          </div>
          {checkout.isError && <InlineError error={checkout.error ?? "Checkout failed"} />}
        </div>

        <DialogFooter>
          <Button
            onClick={() => valid && checkout.mutate(parsed)}
            disabled={!valid || checkout.isPending}
          >
            {checkout.isPending ? "Redirecting…" : "Continue to secure checkout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
